import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '@/hooks/useAuthContext';
import { useHoldRequests } from '@/hooks/useHoldRequests';
import { DashboardMetrics } from '../DashboardMetrics';
import { Card } from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorMessage from '@/components/common/ErrorMessage';
import { formatDate, formatLocalDate, getStatusColor, formatStatus } from '@/lib/utils';
import type { HoldRequest } from '@/types';

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'partial_response_received', label: 'Partial Response' },
  { value: 'request_completed_active', label: 'Completed Active' },
  { value: 'request_expired', label: 'Expired' },
  { value: 'needs_review', label: 'Needs Review' },
];

const involvementOptions = [
  { value: 'all', label: 'All' },
  { value: 'created', label: 'Created by Me' },
  { value: 'collaborating', label: 'Collaborating On' },
];

const statusLabels: Record<string, string> = {
  open: 'Open',
  partial_response_received: 'Partial Response',
  request_completed_active: 'Completed Active',
  request_expired: 'Expired',
  needs_review: 'Needs Review',
};

export function SalesLandingPage() {
  const { user } = useAuthContext();
  const [statusFilter, setStatusFilter] = useState('all');
  const [involvementFilter, setInvolvementFilter] = useState('all');

  // Build query params — involvement filter is handled server-side for sales_ae role
  const queryParams: Record<string, string | number> = {};
  if (involvementFilter !== 'all') {
    queryParams.involvement = involvementFilter;
  }
  queryParams.limit = 100;

  const queryString = new URLSearchParams(
    Object.entries(queryParams).map(([key, value]) => [key, String(value)])
  ).toString();

  const { data: response, isLoading, error } = useHoldRequests(queryString);

  // Calculate overview metrics from server-provided status counts (covers ALL matching records).
  // OPE-386: exclude workflow_status='cancelled' from activeRequests — the backend statusCounts
  // projection now maps cancelled holds to the 'cancelled' key, so open/partial counts are clean.
  const metrics = React.useMemo(() => {
    const counts = response?.statusCounts || {};
    const activeRequests = (counts.open || 0) + (counts.partial_response_received || 0);
    const pendingActions = counts.partial_response_received || 0;
    // completedToday can't be derived from status counts alone — use the page data as an approximation
    const today = new Date().toDateString();
    const completedToday = (response?.holdRequests || []).filter(
      (r: HoldRequest) =>
        r.workflowStatus !== 'cancelled' &&
        r.derivedStatus === 'request_completed_active' &&
        new Date(r.updatedAt).toDateString() === today
    ).length;

    return { activeRequests, pendingActions, completedToday };
  }, [response]);

  // Filter requests by status.
  // OPE-386: cancelled holds have a frozen derivedStatus and render as "Cancelled",
  // so exclude them from specific derived-status filters (mirrors MyRequests.tsx).
  const allRequests = response?.holdRequests || [];
  const filteredRequests = allRequests.filter((request: HoldRequest) => {
    if (statusFilter === 'all') return true;
    if (request.workflowStatus === 'cancelled') return false;
    return request.derivedStatus === statusFilter;
  });

  return (
    <div className="space-y-6">
      {/* Page heading */}
      <div>
        <h1 className="text-3xl font-bold text-neutral-900">Hold Request Overview</h1>
        <p className="mt-2 text-neutral-600">
          Welcome back, {user?.firstName}! Here's a summary of your hold requests.
        </p>
      </div>

      {/* Overview Metrics — first row only */}
      <DashboardMetrics metrics={metrics} />

      {/* My Requests Section */}
      <div className="space-y-4">
        {/* Section header with CTA */}
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-semibold text-neutral-900">My Requests</h2>
            <p className="text-sm text-neutral-600">
              {involvementFilter === 'created'
                ? 'Hold requests you have submitted'
                : involvementFilter === 'collaborating'
                  ? 'Hold requests where you are a collaborator'
                  : 'Hold requests you created or are collaborating on'}
            </p>
          </div>
          <Link to="/holds/create">
            <Button className="font-bold">Create Hold Request</Button>
          </Link>
        </div>

        {/* Filters and count */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <label htmlFor="involvement-filter" className="text-sm font-medium text-neutral-600 whitespace-nowrap">
              Involvement:
            </label>
            <Select
              id="involvement-filter"
              value={involvementFilter}
              onChange={(e) => setInvolvementFilter(e.target.value)}
              className="w-44"
              options={involvementOptions}
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="status-filter" className="text-sm font-medium text-neutral-600 whitespace-nowrap">
              Status:
            </label>
            <Select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-44"
              options={statusOptions}
            />
          </div>
          <div className="text-sm text-neutral-400">
            {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Request list */}
        {isLoading ? (
          <div className="flex justify-center items-center min-h-64">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <ErrorMessage message="Failed to load your requests" />
        ) : filteredRequests.length === 0 ? (
          <Card className="text-center py-12">
            <div className="text-neutral-400">
              {statusFilter === 'all' ? (
                <>
                  <p className="text-lg mb-2">No requests found</p>
                  <p>Get started by creating your first hold request.</p>
                  <Link to="/holds/create" className="mt-4 inline-block">
                    <Button>Create Request</Button>
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-lg mb-2">No {statusLabels[statusFilter]?.toLowerCase() || statusFilter} requests</p>
                  <p>Try changing the status filter to see more requests.</p>
                </>
              )}
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request: HoldRequest) => (
              <Card key={request.id} className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Link
                        to={`/holds/${request.id}`}
                        className="text-lg font-semibold text-neutral-900 hover:text-outfront-purple"
                      >
                        {request.requestNumber}
                      </Link>
                      {/* OPE-386 (c): workflowStatus='cancelled' overrides derivedStatus — routed through formatStatus */}
                      <Badge className={getStatusColor(request.derivedStatus, request.workflowStatus)}>
                        {formatStatus(request.derivedStatus, request.workflowStatus)}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-neutral-600">
                      <div className="space-y-1">
                        <div>
                          <span className="font-medium">Advertiser:</span> {request.advertiser}
                        </div>
                        <div>
                          <span className="font-medium">Category:</span> {request.businessCategory}
                        </div>
                        <div>
                          <span className="font-medium">Region:</span> {request.salesAe?.primaryRegion || 'N/A'}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div>
                          <span className="font-medium">Created:</span> {formatLocalDate(request.createdAt)}
                        </div>
                        <div>
                          <span className="font-medium">Updated:</span> {formatLocalDate(request.updatedAt)}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div>
                          <span className="font-medium">Line Items:</span> {request.totalLineItems || 0}
                        </div>
                        <div>
                          <span className="font-medium">Hold Start:</span>{' '}
                          {request.holdStartDate ? formatDate(request.holdStartDate) : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="ml-4">
                    <Link to={`/holds/${request.id}`}>
                      <Button variant="outline" size="sm">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
